import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router';

import {
  type AppNotification,
  flattenNotifications,
  type NotificationTranslate,
  notificationTarget,
  notificationText,
  useMarkAllRead,
  useMarkNotificationRead,
  useNotifications,
} from '@/entities/collab';
import { useViewer } from '@/entities/viewer';
import { formatDateTime } from '@/shared/lib/datetime';
import { Avatar, Button, Combobox, EmptyState, ErrorState, Icons, LoadingState } from '@/shared/ui';

import styles from './NotificationsPage.module.css';

type ReadState = 'all' | 'unread' | 'read';

/**
 * The notification history: every notification of the last 30 days, newest first, with an
 * unread / read filter and pagination. Opening one marks it read and goes to its target; a
 * target that no longer exists (or that the viewer may no longer open) is said plainly and the
 * item is only marked read - nothing about it is revealed.
 */
export function NotificationsPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const viewer = useViewer();
  const zone = viewer.status === 'authenticated' ? viewer.user.timezone : undefined;
  const [params, setParams] = useSearchParams();
  const requested = params.get('show');
  const state: ReadState = requested === 'unread' || requested === 'read' ? requested : 'all';
  const query = useNotifications({ readState: state === 'read' ? 'all' : state });
  const markRead = useMarkNotificationRead();
  const markAll = useMarkAllRead();
  const all = flattenNotifications(query.data);
  // "Read" is the complement of the server's unread filter: filter the loaded pages.
  const items = state === 'read' ? all.filter((n) => n.read_at !== null) : all;
  const unread = query.data?.pages[0]?.unread_count ?? 0;

  const open = (n: AppNotification) => {
    if (n.read_at === null) markRead.mutate(n.id);
    const target = notificationTarget(n);
    if (target) void navigate(target);
  };

  return (
    <div className={styles.page}>
      <header className={styles.head}>
        <h1 className={styles.title}>{t('notifications.history.title')}</h1>
        <div className={styles.tools}>
          <div className={styles.filter}>
            <Combobox
              label={t('notifications.history.filterLabel')}
              value={state}
              size="sm"
              options={[
                { value: 'all', label: t('notifications.history.filterAll') },
                { value: 'unread', label: t('notifications.history.filterUnread') },
                { value: 'read', label: t('notifications.history.filterRead') },
              ]}
              onChange={(next) => {
                const nextParams = new URLSearchParams(params);
                if (next === 'all') nextParams.delete('show');
                else nextParams.set('show', next);
                setParams(nextParams, { replace: true });
              }}
            />
          </div>
          <Button
            size="sm"
            variant="ghost"
            disabled={unread === 0 || markAll.isPending}
            onClick={() => {
              markAll.mutate();
            }}
          >
            {t('notifications.markAll')}
          </Button>
        </div>
      </header>
      <p className={styles.note}>{t('notifications.history.retention')}</p>
      {query.isPending && <LoadingState />}
      {query.isError && <ErrorState error={query.error} onRetry={() => void query.refetch()} />}
      {query.isSuccess && items.length === 0 && (
        <EmptyState title={t('notifications.history.empty')} />
      )}
      <ul className={styles.list} aria-label={t('notifications.history.title')}>
        {items.map((n) => {
          const { title, detail } = notificationText(t as unknown as NotificationTranslate, n);
          const read = n.read_at !== null;
          const target = notificationTarget(n);
          return (
            <li key={n.id}>
              <button
                type="button"
                className={`${styles.item} ${read ? '' : styles.unread}`}
                data-read={read ? 'true' : 'false'}
                data-testid="history-item"
                onClick={() => {
                  open(n);
                }}
              >
                {n.actor ? (
                  <Avatar id={n.actor.user_id} name={n.actor.display_name} size="sm" />
                ) : (
                  <span className={styles.systemIcon} aria-hidden>
                    <Icons.bell />
                  </span>
                )}
                <span className={styles.body}>
                  <span className={styles.text}>{title}</span>
                  {detail && <span className={styles.detail}>{detail}</span>}
                  {target === null && (
                    <span className={styles.detail}>{t('notifications.history.goneTarget')}</span>
                  )}
                  <span className={styles.when}>
                    {n.actor ? `${n.actor.display_name} · ` : ''}
                    {formatDateTime(n.created_at, i18n.language, zone)}
                  </span>
                </span>
                {!read && <span className={styles.dot} aria-label={t('notifications.unread')} />}
              </button>
            </li>
          );
        })}
      </ul>
      {query.hasNextPage && (
        <div className={styles.more}>
          <Button
            disabled={query.isFetchingNextPage}
            onClick={() => {
              void query.fetchNextPage();
            }}
          >
            {t('notifications.loadMore')}
          </Button>
        </div>
      )}
    </div>
  );
}
